/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';
import { ErrorCodes, Error as FoundationError } from 'fl-foundation';
import {
  AdInfo,
  BUFFERING_STATES,
  PLAYBACK_STATES,
  Player,
  SEEKING_STATES,
} from 'fl-player-interface';
import { DataService } from 'src/app/data.service';
import { ResourceVm } from 'qp-ui-interface';
import { Subject } from 'rxjs';
import { PlaybackService } from 'src/app/shared/services/playback.service';
import { getErrorData } from 'src/app/shared/utils/EventUtlis';
import {
  getAdInfo,
  getLanguageString,
} from 'src/app/shared/utils/PlaybackUtils';
import { ConfigService } from '../../../shared/services/config.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { TabService } from '../../../shared/services/tabs/tab.service';
import { WindowRefService } from '../../../shared/services/windowRef.service';
import { ErrorEvents } from '../../../shared/utils/ErrorUtils';
import { EventName, PlaybackEventName } from '../enums/EventName';
import { PageType } from '../enums/PageType';
import { NewrelicPlayerStore } from '../store/newrelic-player.store';
import {
  getNRPlaybackStatistics,
  getPlaybackDrmInfo,
  getSubtitleInfo,
} from '../utils/playbackEventUtils';
import { PageInfo, PlaybackInfo, VODInfo } from './../model/newrelic.model';
import { NewrelicService } from './newrelic.service';
import { ContentType } from 'src/app/shared/enums/contentType';
import { LanguageService } from 'src/app/language.service';
import { PlayerService } from 'src/app/player/player.service';
import { takeUntil } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { getPreferredLanguages } from 'src/app/shared/utils/deviceUtils';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class NrPlaybackSessionService implements OnDestroy {
  private internalPlayer: Player | undefined;
  private playbackInfo: PlaybackInfo;
  private resource: ResourceVm;
  private playbackStartedTimeInS: number;
  private progressInterval;
  private currentStartedInitialTime = 0;
  private firstLoad = true;
  private destroy$ = new Subject<void>();
  private onVariantChange = false;
  private autoPreview = false;
  private currentPlaybackState: PLAYBACK_STATES;
  private adClickUrl: string;
  private isAdPlaying = false;
  private playbackStartSentForResource: {
    resourceId: string | null;
    sent: boolean;
  } = { resourceId: null, sent: false };
  public isErrorOccurred = false;

  private adEndCallback: (event: any) => void;
  private adStartCallback: (event: any) => void;
  private onAdBreakStartCallback: (event: any) => void;
  private onAdBreakEndCallback: (event: any) => void;
  private onAdErrorCallback: (event: any) => void;
  private onAdTrackingCallback: (event: any) => void;
  private previousNetworkState: 'online' | 'offline' | null = null;
  private endTime: number;

  constructor(
    private newrelicService: NewrelicService,
    private newrelicPlayerStore: NewrelicPlayerStore,
    private tabService: TabService,
    private configService: ConfigService,
    private navigationService: NavigationService,
    private windowRef: WindowRefService,
    private readonly playerService: PlayerService,
    private readonly playbackService: PlaybackService,
    private readonly languageService: LanguageService,
    private readonly dataService: DataService,
    private readonly localStorage: LocalStorageService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPlaybackInfo(): PlaybackInfo {
    return this.playbackInfo;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  initialize(
    resource: ResourceVm,
    streamId?: string,
    mediaUrl?: string,
    autoPreview?: boolean,
    adClickUrl?: string
  ) {
    this.playbackInfo = {
      playbackURL: mediaUrl,
      streamId,
    };
    this.adClickUrl = adClickUrl;

    this.firstLoad = true;
    this.resource = resource;
    this.autoPreview = autoPreview;

    this.playbackStartSentForResource = {
      resourceId: resource.id,
      sent: false,
    };
    this.isErrorOccurred = false;
  }

  attachPlayer(
    player: Player,
    isAdEnabled: boolean = false,
    resourceId: string
  ): void {
    this.resetPlaybackStartedTime();
    this.initStartedTime();
    this.internalPlayer = player;
    this.newrelicPlayerStore.setPlayerInstance(this.internalPlayer);
    this.subscribeToInternalPlayerEvents();
    if (isAdEnabled) {
      this.subscribeAdPlayerEvents();
    }
    if (
      resourceId === this.playbackStartSentForResource.resourceId &&
      !this.playbackStartSentForResource.sent
    ) {
      this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_START);
      this.playbackStartSentForResource = {
        resourceId: resourceId,
        sent: true,
      };
    }
    this.playerService.playerState$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (playerNetworkState) =>
            playerNetworkState.networkState &&
            this.previousNetworkState !== playerNetworkState.networkState
        )
      )
      .subscribe({
        next: (playerNetworkState) => {
          this.previousNetworkState = playerNetworkState.networkState;
          this.sendNewrelicEvents(PlaybackEventName.NETWORK_CHANGE);
        },
        error: (error) => {
          console.error('Error in network state subscription:', error);
        },
      });
  }

  public onError = (error: FoundationError, errorType?: ErrorEvents): void => {
    let customAttr = {};
    const isServerError =
      error &&
      typeof error.doesBelongToCategory === 'function' &&
      error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_SERVER);
    let errorCode = error?.errorCode;
    let errorMessage = error?.errorMessage;
    const errorDescription = error?.internalError;

    if (isServerError) {
      errorCode = isServerError
        ? error?.internalError?.errorCode
        : error?.errorCode;
      errorMessage = isServerError
        ? error?.internalError?.errorMessage
        : error?.errorMessage;
    }
    customAttr = {
      errorCode,
      errorMessage,
      errorType,
      errorDescription,
      internalError: error?.internalError?.errorCode,
      lastPlayedVariant:
        this.internalPlayer?.playbackStatistics?.variantBitrate || 0,
      lastAttemptedVariant:
        this.internalPlayer?.playbackStatistics?.variantBitrate || 0,
      highestVariantBandwidthKb:
        this.internalPlayer?.availableBitrates[
          this.internalPlayer?.availableBitrates?.length - 1
        ] || 0,
      lowestVariantBandwidthKb:
        this.internalPlayer?.availableBitrates?.[0] || 0,
    };
    this.sendNewrelicEvents(PlaybackEventName.ERROR, customAttr);
    // this.detachPlayer();
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  public errorEvent = (
    error: FoundationError,
    errorType?: ErrorEvents,
    additionalAttrs?: {}
  ): void => {
    let customAttr = {};
    const errorCode = error?.errorCode;
    const errorMessage = error?.errorMessage;
    const errorDescription = error?.internalError;

    customAttr = {
      errorCode,
      errorMessage,
      errorType,
      errorDescription,
      internalError: error?.internalError?.errorCode,
      lastPlayedVariant:
        this.internalPlayer?.playbackStatistics.variantBitrate || 0,
      lastAttemptedVariant:
        this.internalPlayer?.playbackStatistics.variantBitrate || 0,
      highestVariantBandwidthKb:
        this.internalPlayer?.availableBitrates[
          this.internalPlayer?.availableBitrates?.length - 1
        ] || 0,
      lowestVariantBandwidthKb: this.internalPlayer?.availableBitrates[0] || 0,
      ...additionalAttrs,
    };
    this.sendNewrelicEvents(PlaybackEventName.ERROR, customAttr);
  };

  detachPlayer(isAdEnabled: boolean = false): void {
    this.newrelicPlayerStore.setPlayerInstance(null);
    this.unsubscribeFromInternalPlayerEvents();
    if (isAdEnabled) {
      this.unsubscribeAdPlayerEvents();
    }
    this.resource = undefined;
    this.internalPlayer = undefined;
    this.onVariantChange = false;
    this.previousNetworkState = null;
    this.destroy$.next();
    this.destroy$.complete();
    this.playbackStartSentForResource = {
      resourceId: null,
      sent: false,
    };
  }

  /**
   * Subscribe to player events.
   *
   * @param player
   */
  private subscribeToInternalPlayerEvents = (): void => {
    if (!this.internalPlayer) return;

    this.internalPlayer.subscribe('contentloaded', this.onContentLoaded);
    this.internalPlayer.subscribe(
      'bufferingstatechanged',
      this.onBufferingStateChanged
    );
    this.internalPlayer.subscribe('streamended', this.onStreamEnded);
    this.internalPlayer.subscribe(
      'seekingstatechanged',
      this.onSeekingStateChanged
    );
    this.internalPlayer.subscribe(
      'playbackstatechanged',
      this.onPlaybackStateChanged
    );
    this.internalPlayer.subscribe('bandwidthchanged', this.onBandwidthChanged);
    /* this.internalPlayer.subscribe('error', (error) => {
      this.onError(error, ErrorEvents.PLAYBACK_ERROR);
    }); */
  };

  /**
   * Unsubscribe from player events.
   *
   * @param player
   */
  private unsubscribeFromInternalPlayerEvents = (): void => {
    if (!this.internalPlayer) return;

    this.internalPlayer.unsubscribe('contentloaded', this.onContentLoaded);
    this.internalPlayer.unsubscribe(
      'bufferingstatechanged',
      this.onBufferingStateChanged
    );
    this.internalPlayer.unsubscribe('streamended', this.onStreamEnded);
    this.internalPlayer.unsubscribe(
      'seekingstatechanged',
      this.onSeekingStateChanged
    );
    this.internalPlayer.unsubscribe(
      'playbackstatechanged',
      this.onPlaybackStateChanged
    );
    this.internalPlayer.unsubscribe(
      'bandwidthchanged',
      this.onBandwidthChanged
    );
    /*   this.internalPlayer.unsubscribe('error', (error) => {
      this.onError(error, ErrorEvents.PLAYBACK_ERROR);
    }); */
  };

  private subscribeAdPlayerEvents(): void {
    if (!this.internalPlayer) return;
    this.adEndCallback = (event) => this.onAdEnd(event); // Store the callback reference
    this.adStartCallback = (event) => this.onAdStart(event); // Store the callback reference
    this.onAdBreakStartCallback = (event) => this.onAdBreakStart(event); // Store the callback reference
    this.onAdBreakEndCallback = (event) => this.onAdBreakEnd(event); // Store the callback reference
    // this.onAdErrorCallback = (error) => this.onAdError(error); // Store the callback reference
    this.onAdTrackingCallback = (event) => this.onAdTracking(event); // Store the callback reference

    this.internalPlayer.subscribe('adstart', this.adStartCallback); // Pass the stored reference directly
    this.internalPlayer.subscribe('adbreakstart', this.onAdBreakStartCallback);
    this.internalPlayer.subscribe('adbreakend', this.onAdBreakEndCallback);
    this.internalPlayer.subscribe('adend', this.adEndCallback); // Pass the stored reference directly
    // this.internalPlayer.subscribe('aderror', this.onAdErrorCallback);
    this.internalPlayer.subscribe(
      'adverttrackingevent',
      this.onAdTrackingCallback
    );
  }

  private unsubscribeAdPlayerEvents(): void {
    if (!this.internalPlayer) return;
    this.internalPlayer.unsubscribe('adstart', this.adStartCallback); // Use the stored reference
    this.internalPlayer.unsubscribe(
      'adbreakstart',
      this.onAdBreakStartCallback
    );
    this.internalPlayer.unsubscribe('adbreakend', this.onAdBreakEndCallback);
    this.internalPlayer.unsubscribe('adend', this.adEndCallback); // Use the stored reference
    // this.internalPlayer.unsubscribe('aderror', this.onAdErrorCallback);
    this.internalPlayer.unsubscribe(
      'adverttrackingevent',
      this.onAdTrackingCallback
    );
  }

  private initStartedTime(): void {
    this.currentStartedInitialTime = this.playbackService.startTime;
  }

  private onBufferingStateChanged = (
    bufferingState: BUFFERING_STATES
  ): void => {
    if (this.isAdPlaying) {
      return;
    }
    switch (bufferingState) {
      case BUFFERING_STATES.ACTIVE: {
        this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_BUFFERING_START);

        break;
      }
      case BUFFERING_STATES.INACTIVE: {
        this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_BUFFERING_END);
        break;
      }
    }
  };

  private startProgressInterval = (): void => {
    try {
      const interval = this.configService.applicationConfig
        .playbackReportingIntervalMs
        ? parseInt(
            this.configService.applicationConfig.playbackReportingIntervalMs,
            10
          )
        : 60000;
      this.stopProgressInterval();
      this.progressInterval = setInterval(() => {
        // Don't send playback progress events during ad playback
        if (this.isAdPlaying) {
          return;
        }
        this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_INPROGRESS, {
          currentPosition:
            Math.round(this.internalPlayer?.currentTime) + ' secs',
        });
      }, interval);
    } catch {}
  };

  private stopProgressInterval = (): void => {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = undefined;
    }
  };

  private onStreamEnded = (): void => {
    // Don't send playback complete events during ad playback
    if (this.isAdPlaying) {
      return;
    }
    this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_COMPLETE, {
      playbackDurationSec: this.internalPlayer
        ? Math.floor(this.internalPlayer?.playbackStatistics?.playTime)
        : 0,
    });
  };

  private onContentLoaded = (): void => {
    this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_PREPARED);
  };

  private onSeekingStateChanged = (seekingState: SEEKING_STATES): void => {
    // Don't send seek events during ad playback
    if (this.isAdPlaying) {
      return;
    }
    let customAttr = {};
    switch (seekingState) {
      case SEEKING_STATES.ACTIVE: {
        customAttr = {
          playbackSeekStartTimeSec: this.internalPlayer?.currentTime || 0,
        };
        this.sendNewrelicEvents(
          PlaybackEventName.PLAYBACK_SEEK_START,
          customAttr
        );
        break;
      }
      case SEEKING_STATES.INACTIVE: {
        customAttr = {
          playbackSeekEndTimeSec: this.internalPlayer?.currentTime || 0,
        };
        this.sendNewrelicEvents(
          PlaybackEventName.PLAYBACK_SEEK_COMPLETE,
          customAttr
        );
        break;
      }
    }
  };

  private onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    if (this.currentPlaybackState === playbackState) {
      return; // Skip if the playback state is unchanged
    }

    if (this.isAdPlaying) {
      return;
    }

    this.currentPlaybackState = playbackState;
    switch (playbackState) {
      case PLAYBACK_STATES.STARTED: {
        if (this.firstLoad) {
          this.playbackStartTimeSec().then(() => {
            this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_STARTED, {
              playbackStartedTimeSec: this.playbackStartedTimeInS || 0,
            });
            this.firstLoad = false;
            this.startProgressInterval();
          });
        } else {
          this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_RESUMED, {
            currentPosition:
              Math.round(this.internalPlayer?.currentTime) + ' secs',
          });
          this.startProgressInterval();
        }
        break;
      }
      case PLAYBACK_STATES.PAUSED: {
        if (this.internalPlayer?.currentTime < this.internalPlayer?.duration) {
          this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_PAUSED, {
            playbackStartedTimeSec: this.playbackStartedTimeInS,
            currentPosition:
              Math.round(this.internalPlayer?.currentTime) + ' secs',
            playbackDurationSec: this.internalPlayer
              ? Math.floor(this.internalPlayer?.playbackStatistics?.playTime)
              : 0,
          });
        }
        this.stopProgressInterval();
        break;
      }
      case PLAYBACK_STATES.STOPPED:
      case PLAYBACK_STATES.IDLE: {
        this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_STOPPED, {
          currentPosition:
            Math.round(this.internalPlayer?.currentTime) + ' secs',
          playbackDurationSec: this.internalPlayer
            ? Math.floor(this.internalPlayer?.playbackStatistics?.playTime)
            : 0,
        });
        this.stopProgressInterval();
        this.unsubscribeFromInternalPlayerEvents();
        this.internalPlayer = undefined;
        break;
      }
      /* case PLAYBACK_STATES.IDLE: {
        console.log('>>>>PLAYBACK_IDLE >>>');
        this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_STOPPED, {
          currentPosition:
            Math.round(this.internalPlayer?.currentTime) + ' secs',
          playbackDurationSec: this.internalPlayer
            ? Math.floor(this.internalPlayer?.playbackStatistics?.playTime)
            : 0,
        });
        this.stopProgressInterval();
        this.unsubscribeFromInternalPlayerEvents();
        this.internalPlayer = undefined;
        break;
      } */
    }
  };

  private onBandwidthChanged = (): void => {
    // Don't send variant change events during ad playback
    if (this.isAdPlaying) {
      return;
    }
    if (
      this.internalPlayer &&
      this.internalPlayer.playbackStatistics &&
      this.onVariantChange
    ) {
      this.sendNewrelicEvents(PlaybackEventName.PLAYBACK_VARIANT_CHANGE);
    }
    this.onVariantChange = true;
  };

  private onAdStart = (event: AdInfo): void => {
    const adInfo = getAdInfo(event, this.adClickUrl);
    this.sendNewrelicEvents(PlaybackEventName.AD_START, adInfo);
  };

  private onAdBreakStart(event: AdInfo): any {
    this.isAdPlaying = true;
    // Stop progress interval to prevent PLAYBACK_INPROGRESS events during ads
    this.stopProgressInterval();
    const adInfo = getAdInfo(event);
    this.sendNewrelicEvents(PlaybackEventName.AD_BREAK_START, adInfo);
    this.endTime = new Date().getTime();
  }

  private onAdTracking(adTrackingEvent): void {
    switch (adTrackingEvent) {
      case 'Ad Skipped': // Ad was skipped
        this.sendNewrelicEvents(PlaybackEventName.AD_SKIP);
        break;
      case 'Ad Click': // Ad LEARN MORE link was clicked
        this.sendNewrelicEvents(PlaybackEventName.AD_VIEW);
        break;
    }
  }

  private onAdEnd = (event: AdInfo): void => {
    const adInfo = getAdInfo(event, this.adClickUrl);
    this.sendNewrelicEvents(PlaybackEventName.AD_END, adInfo);
  };

  private onAdBreakEnd(event: AdInfo): any {
    this.isAdPlaying = false;
    const adInfo = getAdInfo(event);
    this.sendNewrelicEvents(PlaybackEventName.AD_BREAK_COMPLETE, adInfo);
    // Restart progress interval if playback is still active
    if (
      this.internalPlayer &&
      this.currentPlaybackState === PLAYBACK_STATES.STARTED
    ) {
      this.startProgressInterval();
    }
  }

  private onAdError(error): any {
    this.isAdPlaying = false;
    // Restart progress interval if playback is still active after ad error
    if (
      this.internalPlayer &&
      this.currentPlaybackState === PLAYBACK_STATES.STARTED
    ) {
      this.startProgressInterval();
    }
    if (!this.internalPlayer) {
      const { errorCode, errorMessage } = getErrorData(error);
      // const errorAttributes = this.getEventAttributes(PlaybackEventName.ERROR);
      this.sendNewrelicEvents(PlaybackEventName.ERROR, {
        errorCode,
        errorDescription: errorMessage,
        errorMessage,
        errorType: PlaybackEventName.AD_PLAYBACK_ERROR,
        internalError: error,
      });
    }
  }

  private shouldSendEvent(eventName: EventName): boolean {
    // If autoPreview is true, only allow specific events
    if (this.autoPreview) {
      const allowedEvents = [
        PlaybackEventName.PLAYBACK_STARTED,
        PlaybackEventName.PLAYBACK_COMPLETE,
        PlaybackEventName.PLAYBACK_STOPPED,
      ];
      return allowedEvents.includes(eventName as PlaybackEventName);
    }
    // If not autoPreview, allow all events
    return true;
  }

  private sendNewrelicEvents(eventName: EventName, customAttr = {}): void {
    try {
      if (this.isErrorOccurred) {
        return;
      }

      if (eventName === PlaybackEventName.ERROR) {
        this.isErrorOccurred = true;
      }

      // Check if event should be sent based on autoPreview flag
      if (
        !this.shouldSendEvent(eventName) &&
        eventName !== PlaybackEventName.ERROR
      ) {
        return;
      }

      const current_screen =
        typeof this.navigationService?.currentScreen === 'string'
          ? this.navigationService?.currentScreen
          : this.navigationService?.currentScreen[
              this.languageService.currLang
            ] || undefined;
      const customAttributes = {
        ...this.playbackInfo,
        ...this.newrelicPlayerStore.getPlayerInfo(),
        ...getNRPlaybackStatistics(
          this.internalPlayer,
          this.resource,
          this.navigationService,
          this.windowRef,
          this.dataService.resolution
        ),
        ...getSubtitleInfo(this.internalPlayer),
        ...getPlaybackDrmInfo(),
        ...this.getPageInfo(),
        ...this.getVODInfo(),
        ...customAttr,
        currentPosition:
          Math.round(this.internalPlayer?.currentTime || 0) + ' secs',
        current_screen: current_screen == 'All' ? 'Home' : current_screen,
        playbackType: this.getPlaybackType(
          this.resource?.contentType as ContentType
        ),
        playbackMode: this.getPlaybackType(
          this.resource?.contentType as ContentType
        ),
        autoPreview: this.autoPreview,
        audioLanguage: this.autoPreview
          ? getLanguageString(this.resource?.inLanguage)
          : this.playerService.audioLanguage,
        subtitleLanguage: this.playerService.subtitleLanguage,
        preferredLanguage: getPreferredLanguages(
          this.dataService.isFromDeeplink
            ? this.localStorage.getItem(KEYS.DL_PREFERRED_LANGUAGES)
            : null
        ),
      };
      this.newrelicService.store.setCustomAttributes(customAttributes);
      if (this.resource) {
        this.newrelicService.sendNewrelicEvents(
          eventName,
          this.resource,
          customAttributes
        );
      }
    } catch (e) {
      console.warn('nr error', e);
    }
  }

  private getPlaybackType(contentType: ContentType): string {
    return contentType === ContentType.LIVE_CHANNEL ||
      contentType === ContentType.LIVE_EVENT ||
      contentType === ContentType.EVENT ||
      contentType === ContentType.CHANNEL
      ? 'Live'
      : 'VOD';
  }

  private getPageInfo(): PageInfo {
    return {
      pageID: PageType.Player,
      tabID: this.tabService?.currentTabId,
    };
  }

  getVODInfo(): VODInfo {
    const isPremium = this.resource?.isPremium ? 'SVOD' : 'Free';
    return {
      vod: this.resource?.isTvod
        ? 'TVOD'
        : this.resource?.isPremium
          ? 'SVOD'
          : undefined,
      vod_type: this.resource?.isTvod ? 'TVOD' : isPremium || undefined,
    };
  }

  private playbackStartTimeSec(): Promise<void> {
    try {
      if (!this.endTime) {
        this.endTime = new Date().getTime();
      }
      this.playbackStartedTimeInS = this.currentStartedInitialTime
        ? (this.endTime - this.currentStartedInitialTime) / 1000
        : 0;
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  resetPlaybackStartedTime(): void {
    this.playbackStartedTimeInS = 0;
    this.currentStartedInitialTime = 0;
    this.firstLoad = true;
    this.endTime = null;
    this.isErrorOccurred = false;
  }
}
